import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Minus, Plus } from "lucide-react";

/**
 * Výřez fotky nebo loga před uložením — stejné ovládání jako logo výrobce:
 * tahem se obrázek posouvá, posuvníkem (kolečkem myši, dvěma prsty)
 * přibližuje. Výsledek se vykreslí do plátna a vrátí jako data URI,
 * takže sedí všude, i v PDF nabídek a protokolů.
 *
 * tvar="foto" — čtverec 256 px (JPEG), obrázek vždy vyplní celý rámeček.
 * tvar="logo" — obdélník 7 : 4 (PNG s průhledností), logo jde i zmenšit.
 * tvar="znak" — čtverec 400 px (PNG s průhledností) pro logo do kolečka/dlaždice
 *   (obchodní partner): kulaté a čtvercové logo se nezmenší do úzkého pruhu.
 * tvar="budova" — fotka domu 3 : 2 (JPEG), vyplní celý rámeček (PDF nabídky).
 * tvar="produkt" — fotka zařízení 3 : 2 na bílém pozadí (JPEG), jde i zmenšit.
 */
const RAMEC = {
  foto: { w: 240, h: 240, ow: 256, oh: 256, min: 1, max: 4, zaobleni: "rounded-[22px]" },
  logo: { w: 280, h: 160, ow: 560, oh: 320, min: 0.3, max: 4, zaobleni: "rounded-icon" },
  znak: { w: 240, h: 240, ow: 400, oh: 400, min: 0.3, max: 4, zaobleni: "rounded-[22px]" },
  budova: { w: 300, h: 200, ow: 1050, oh: 700, min: 1, max: 4, zaobleni: "rounded-icon" },
  produkt: { w: 300, h: 200, ow: 900, oh: 600, min: 0.3, max: 4, zaobleni: "rounded-icon" },
};
const VYPLNIT = new Set(["foto", "budova"]);          // obrázek vždy vyplní rámeček
const JPEG = new Set(["foto", "budova", "produkt"]);  // bez průhlednosti, menší soubor

export default function OrezObrazku({ src, tvar = "foto", onHotovo, onZrusit }) {
  const r = RAMEC[tvar] || RAMEC.foto;
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });   // posun středu obrázku v px rámečku
  const [chyba, setChyba] = useState("");
  const prsty = useRef(new Map());
  const tah = useRef(null);

  useEffect(() => {
    const i = new Image();
    i.onload = () => setImg(i);
    i.onerror = () => setChyba("Tohle není obrázek, který umíme otevřít (zkus PNG nebo JPG).");
    i.src = src;
  }, [src]);

  // Základní měřítko: fotka rámeček vyplní, logo se do něj celé vejde.
  const zaklad = img
    ? (VYPLNIT.has(tvar) ? Math.max(r.w / img.width, r.h / img.height) : Math.min(r.w / img.width, r.h / img.height))
    : 1;
  const s = zaklad * zoom;
  const sirka = img ? img.width * s : 0;
  const vyska = img ? img.height * s : 0;

  // Fotka nesmí odjet tak, aby v rámečku zůstal prázdný okraj; logo jen nesmí zmizet.
  const omez = (p, z = zoom) => {
    if (!img) return p;
    const sw = img.width * zaklad * z, sh = img.height * zaklad * z;
    const mx = VYPLNIT.has(tvar) ? Math.max(0, (sw - r.w) / 2) : (sw + r.w) / 2 - 12;
    const my = VYPLNIT.has(tvar) ? Math.max(0, (sh - r.h) / 2) : (sh + r.h) / 2 - 12;
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };
  const nastavZoom = (z) => {
    const nz = Math.round(Math.max(r.min, Math.min(r.max, z)) * 100) / 100;
    setZoom(nz);
    setPos((p) => omez(p, nz));
  };

  const dolu = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    prsty.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (prsty.current.size === 1) tah.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
    if (prsty.current.size === 2) {
      const [a, b] = [...prsty.current.values()];
      tah.current = { stisk: Math.hypot(a.x - b.x, a.y - b.y), zoom };
    }
  };
  const pohyb = (e) => {
    if (!prsty.current.has(e.pointerId) || !tah.current) return;
    prsty.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (prsty.current.size >= 2 && tah.current.stisk) {
      const [a, b] = [...prsty.current.values()];
      nastavZoom(tah.current.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / tah.current.stisk));
    } else if (tah.current.px !== undefined) {
      setPos(omez({ x: tah.current.px + e.clientX - tah.current.x, y: tah.current.py + e.clientY - tah.current.y }));
    }
  };
  const nahoru = (e) => {
    prsty.current.delete(e.pointerId);
    // Po zvednutí jednoho prstu pokračuje posun od aktuálního místa.
    const zbyly = [...prsty.current.values()][0];
    tah.current = zbyly ? { x: zbyly.x, y: zbyly.y, px: pos.x, py: pos.y } : null;
  };
  // Kolečko myši: nativní posluchač, aby se přitom neposouvala stránka pod dialogem.
  const nahled = useRef(null);
  const zoomRef = useRef(null);
  zoomRef.current = (dy) => nastavZoom(zoom * (dy < 0 ? 1.08 : 1 / 1.08));
  useEffect(() => {
    const el = nahled.current;
    if (!el) return undefined;
    const kolecko = (e) => { e.preventDefault(); zoomRef.current(e.deltaY); };
    el.addEventListener("wheel", kolecko, { passive: false });
    return () => el.removeEventListener("wheel", kolecko);
  }, []);

  const pouzit = () => {
    if (!img) return;
    const c = document.createElement("canvas");
    c.width = r.ow; c.height = r.oh;
    const ctx = c.getContext("2d");
    const k = r.ow / r.w;
    if (JPEG.has(tvar)) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, (r.w / 2 + pos.x - sirka / 2) * k, (r.h / 2 + pos.y - vyska / 2) * k, sirka * k, vyska * k);
    onHotovo(JPEG.has(tvar) ? c.toDataURL("image/jpeg", 0.86) : c.toDataURL("image/png"));
  };

  // Portál do <body>: dialog nesmí oříznout karta s overflow-hidden ani transformace rodiče.
  return createPortal(
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="orez-obrazku">
      <div className="absolute inset-0 bg-black/40" onClick={onZrusit} />
      <div className="relative bg-white w-full md:max-w-[400px] md:rounded-card rounded-t-sheet shadow-panel">
        <div className="border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="text-[16px] font-800">{tvar === "foto" || tvar === "budova" || tvar === "produkt" ? "Upravit fotku" : "Upravit logo"}</h2>
          <button type="button" onClick={onZrusit} className="text-muted hover:text-ink" aria-label="Zavřít"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex justify-center">
            <div onPointerDown={dolu} onPointerMove={pohyb} onPointerUp={nahoru} onPointerCancel={nahoru} ref={nahled}
              data-testid="orez-nahled" title="Tahem posuneš, kolečkem přiblížíš"
              className={`relative overflow-hidden border border-line-strong bg-white touch-none cursor-grab active:cursor-grabbing ${r.zaobleni}`}
              style={{ width: r.w, height: r.h }}>
              {img && (
                <img src={src} alt="" draggable={false} className="absolute max-w-none select-none pointer-events-none"
                  style={{ width: sirka, height: vyska, left: r.w / 2 + pos.x - sirka / 2, top: r.h / 2 + pos.y - vyska / 2 }} />
              )}
            </div>
          </div>
          {chyba ? <p className="t-meta text-danger">{chyba}</p> : (
            <>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => nastavZoom(zoom - 0.1)} data-testid="orez-oddalit" title="Oddálit"
                  className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg text-sec flex items-center justify-center"><Minus size={13} /></button>
                <input type="range" min={r.min} max={r.max} step="0.01" value={zoom} data-testid="orez-zoom"
                  onChange={(e) => nastavZoom(Number(e.target.value))} className="flex-1 accent-accent" aria-label="Přiblížení" />
                <button type="button" onClick={() => nastavZoom(zoom + 0.1)} data-testid="orez-priblizit" title="Přiblížit"
                  className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg text-sec flex items-center justify-center"><Plus size={13} /></button>
                <span className="amount t-meta text-sec w-[44px] text-right">{Math.round(zoom * 100)} %</span>
              </div>
              <div className="flex items-center gap-4 t-meta">
                <button type="button" onClick={() => { setZoom(1); setPos({ x: 0, y: 0 }); }} className="font-600 text-sec hover:text-ink">Vycentrovat</button>
                <span className="text-muted-2">Tahem obrázek posuneš.</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={onZrusit} className="btn-ghost ml-auto">Zrušit</button>
            <button type="button" onClick={pouzit} disabled={!img} className="btn-primary" data-testid="orez-pouzit">Použít</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
